<template>
    <div class="xl:pl-4 my-8 xl:mt-0 w-full xl:w-2/5">
        <h2 class="mb-2">Customer Pricing</h2>

        <div class="flex space-x-4 my-1">
            <div><span class="font-bold">$: </span> Set dollar amount</div>
            <div><span class="font-bold">M%: </span> Margin percentage</div>
        </div>

        <div class="rounded-t overflow-auto border border-b-0" v-show="form.pricing.length > 0">
            <table class="table w-full">
                <thead>
                    <tr>
                        <th class="text-left whitespace-nowrap">Item #</th>
                        <th class="text-left">Product</th>
                        <th class="text-left">UofM</th>
                        <th class="text-right">Cost</th>
                        <th class="text-right">Base</th>
                        <th class="text-left">Price</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="pricing in sortedPricing" :key="pricing.id">
                        <td class="leading-tight text-sm">{{ pricing.product_id }}</td>
                        <td class="leading-tight text-sm">{{ pricing.title }}</td>
                        <td class="leading-tight text-sm">{{ pricing.size }}</td>
                        <td class="text-right text-sm">{{ pricing.cost }}</td>
                        <td class="text-right text-sm">{{ pricing.base_price }}</td>
                        <td class="text-left">
                            <div class="inline-flex justify-end">
                                <select v-model="form.pricing[pricing.orig_index].modifier_type" class="rounded-l rounded-r-none">
                                    <option value="fixed">$</option>
                                    <option value="margin">M%</option>
                                </select>

                                <input
                                    v-model.number="form.pricing[pricing.orig_index].modifier_amount"
                                    type="number"
                                    step="any"
                                    class="border-l-0"
                                    :class="{
                                        'rounded-none': form.pricing[pricing.orig_index].modifier_type === 'margin',
                                        'rounded-l-none rounded-r': form.pricing[pricing.orig_index].modifier_type !== 'margin',
                                    }"
                                />
                                <div v-show="form.pricing[pricing.orig_index].modifier_type === 'margin'" class="border-l-0 rounded-l-none rounded-r bg-gray whitespace-nowrap p-1">
                                    % = {{ isNaN(form.pricing[pricing.orig_index].price) ? '...' : form.pricing[pricing.orig_index].price }}
                                </div>
                            </div>

                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.price`]" />
                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.modifier_type`]" />
                            <jet-input-error :message="form.errors[`pricing.${pricing.orig_index}.modifier_amount`]" />
                        </td>
                        <td>
                            <jet-secondary-button
                                class="leading-none py-1 px-2 align-middle"
                                @click="remove(pricing.product_id)"
                            >
                                <img src="/images/icons/trash_w.svg" width="18" height="18" />
                            </jet-secondary-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex items-center gap-x-3 mt-2">
            <multiselect
                v-model="selectedProduct"
                :options="productOptions"
                :searchable="true"
                placeholder="Select product"
                style="min-height: 32px"
            />

            <jet-secondary-button type="button" @click="add" :disabled="selectedProduct === null">
                Add
            </jet-secondary-button>
        </div>
    </div>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import Multiselect from '@vueform/multiselect';
import { calculatePricingLevel } from '@/helper';

export default {
    components: {
        JetInput,
        JetInputError,
        JetSecondaryButton,
        Multiselect,
    },

    props: ['form', 'products'],

    data () {
        return {
            selectedProduct: null
        }
    },

    computed: {
        productOptions () {
            const unusedProducts = this.products.filter(p => {
                return this.form.pricing.findIndex(p2 => p2.product_id == p.id) === -1;
            });

            return unusedProducts.map(p => {
                return {
                    value: p.id,
                    label: p.id + ' - ' + p.title + ' - ' + p.size,
                }
            });
        },

        sortedPricing () {
            let sortedPricing = JSON.parse(JSON.stringify(this.form.pricing));
            sortedPricing.forEach((element, index) => {
                element.orig_index = index;
            });

            return sortedPricing.sort((a, b) => {
                if (a.title < b.title)return -1;
                if (a.title > b.title) return 1;
                return 0;
            });
        }
    },

    methods: {
        remove (productId) {
            const index = this.form.pricing.findIndex(p => p.product_id === productId);

            if (index !== -1) {
                this.form.pricing.splice(index, 1);
            }
        },

        add () {
            const product = this.products.find(p => p.id === this.selectedProduct);

            if (!product) {
                return;
            }

            this.form.pricing.push({
                product_id: this.selectedProduct,
                price: null,
                base_price: product.price,
                cost: product.cost,
                title: product.title,
                size: product.size,
            });

            this.selectedProduct = null;
        },

        calculatePricingLevel (pricingLevel, cost, price) {
            return this.roundDecimals(calculatePricingLevel(pricingLevel, cost, price));
        },
    },

    watch:{
        'form.pricing':{
            handler(newValue) {
                this.$nextTick(() => {
                    for (let pricing of newValue){
                        pricing.price = this.calculatePricingLevel(pricing, pricing.cost, pricing.base_price)
                    }
                });
                
            },
            deep: true
        }
    }
}
</script>

<style scoped>
    input[type="number"] {
        @apply py-0 px-1;
        max-width: 75px;
    }
</style>